// import { BASE_URL } from 'config/apiConfig';
import apiClient from 'services/_infra/apiClient';
const BASE_URL = import.meta.env.PUBLIC_API_URL; // TODO: Check with Jacob and Joey how to handle this properly
/**
 * @typedef {'checkpoint' | 'origin' | 'destination'} TravelNodeType
 *
 * @typedef {Object} TravelNode
 * @property {string|number} [id] // assigned by backend on create
 * @property {string} name
 * @property {[number, number]} coords
 * @property {TravelNodeType} type
 * @property {string} address
 * @property {number} radius
 * @property {number} waitTime
 * @property {number} segmentTime
 * @property {number} bonusTime
 * @property {number} totalTimeFromStart
 *
 * @typedef {Object} TravelRoute
 * @property {string} [id] // assigned by backend
 * @property {string} name
 * @property {TravelNode[]} nodes
 * @property {string} encodedPolyline
 * @property {object[]} [legs] // legacy; omitted for per-node fields
 * @property {'fastest' | 'shortest' | 'traffic_aware'} routeOptimization
 * @property {Record<string, any>} [extra] Additional backend fields to include.
 */

const normalizeNumber = (value, fallback = null) => {
  if (value === null || value === undefined) return fallback;
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const normalizePoint = (pt) => {
  if (!pt) return null;
  if (Array.isArray(pt) && pt.length >= 2) {
    const lat = normalizeNumber(pt[0], null);
    const lng = normalizeNumber(pt[1], null);
    if (lat == null || lng == null) return null;
    return { lat, lng };
  }
  const lat = normalizeNumber(pt.lat ?? pt.latitude, null);
  const lng = normalizeNumber(pt.lng ?? pt.longitude, null);
  if (lat == null || lng == null) return null;
  return { lat, lng };
};

const normalizeGeometry = (node = {}) => {
  const geo = node.geometry || {};
  const rawType =
    geo.kind || geo.type || node.geometryType || node.shapeMode || node.kind || 'circle';
  const rawTypeStr = `${rawType}`.toLowerCase();
  // 'zone' is not a geometry shape — map to the underlying shape type
  const kind =
    rawTypeStr === 'zone'
      ? Array.isArray(geo.points) && geo.points.length > 0
        ? 'polygon'
        : 'circle'
      : ['circle', 'crossline', 'polygon'].includes(rawTypeStr)
        ? rawTypeStr
        : 'circle';

  const radiusMeters =
    normalizeNumber(geo.radiusMeters ?? geo.radius ?? node.radius ?? 75, 75) || 75;
  const angleDeg =
    geo.angleDeg != null
      ? normalizeNumber(geo.angleDeg, null)
      : geo.angleRad != null
        ? (normalizeNumber(geo.angleRad, null) || 0) * (180 / Math.PI)
        : geo.handleBearing != null
          ? (normalizeNumber(geo.handleBearing, null) || 0) * (180 / Math.PI)
          : null;

  if (kind === 'crossline') {
    const pts = Array.isArray(geo.points) ? geo.points.map(normalizePoint).filter(Boolean) : [];
    return {
      kind: 'crossline',
      points: pts.slice(0, 2),
      radiusMeters: radiusMeters,
      angleDeg: angleDeg ?? undefined,
    };
  }

  if (kind === 'polygon') {
    const pts = Array.isArray(geo.points) ? geo.points.map(normalizePoint).filter(Boolean) : [];
    return {
      kind: 'polygon',
      points: pts,
    };
  }

  // default circle
  return {
    kind: 'circle',
    radiusMeters,
  };
};

const parseDurationSeconds = (val) => {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const n = parseFloat(val.replace('s', ''));
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const normalizeNode = (node = {}, idx = 0) => {
  const location = normalizePoint(node.coords) ||
    normalizePoint([node.lat, node.lng]) ||
    normalizePoint(node.location) || { lat: 0, lng: 0 };

  const legSource = node.leg && typeof node.leg === 'object' ? node.leg : {};
  const hasExplicitLegData =
    legSource.distanceMeters != null ||
    legSource.durationSeconds != null ||
    legSource.duration != null ||
    legSource.encodedPolyline != null ||
    legSource.polyline != null ||
    node.distanceMeters != null ||
    node.durationSeconds != null ||
    node.duration != null ||
    node.encodedPolyline != null ||
    node.polyline != null;
  const travelMode = (legSource.travelMode || node.travelMode || node.mode || 'DRIVING')
    .toString()
    .toUpperCase();
  const distanceMeters = normalizeNumber(legSource.distanceMeters ?? node.distanceMeters, null);
  const durationSeconds = parseDurationSeconds(
    legSource.durationSeconds ?? legSource.duration ?? node.durationSeconds ?? node.duration,
  );
  const leg = hasExplicitLegData
    ? {
        distanceMeters: distanceMeters ?? 0,
        durationSeconds: durationSeconds ?? 0,
        encodedPolyline:
          legSource.encodedPolyline ??
          legSource.polyline ??
          node.encodedPolyline ??
          node.polyline ??
          null,
        travelMode: travelMode === 'WALKING' ? 'WALKING' : 'DRIVING',
      }
    : null;

  const waitMinutes = normalizeNumber(
    node.stopTimeMinutes ?? node.waitTime ?? node.timing?.waitMinutes,
    0,
  );
  const bonusMinutes = normalizeNumber(
    node.timeWindowMinutes ?? node.bonusTime ?? node.timing?.bonusMinutes,
    0,
  );
  const totalMinutesFromStart = normalizeNumber(
    node.totalMinutesFromStart ?? node.timing?.totalMinutesFromStart,
    null,
  );

  const normalizedGeometry = normalizeGeometry(node);
  // Promote zoneId to the top level of the node so the backend (and read-back
  // normalizers) can find it without having to inspect geometry internals.
  const zoneId = node.zoneId ?? node.zone_id ?? normalizedGeometry?.zoneId ?? null;

  return {
    id: node.id,
    name: node.name ?? `Checkpoint ${idx + 1}`,
    type: node.type ?? node.role ?? 'checkpoint',
    tourId: node.tourId,
    location,
    address: node.address ?? '',
    timing: {
      waitMinutes,
      bonusMinutes,
      totalMinutesFromStart: totalMinutesFromStart ?? undefined,
    },
    geometry: normalizedGeometry,
    ...(zoneId != null ? { zoneId } : {}),
    ...(leg ? { leg } : {}),
  };
};

const buildRoutePayload = (route) => {
  const extras =
    route && typeof route.extra === 'object' && route.extra !== null && !Array.isArray(route.extra)
      ? route.extra
      : {};

  const normalizedNodes = Array.isArray(route?.nodes)
    ? route.nodes.map((n, idx) => normalizeNode(n, idx))
    : [];

  const payloadNodes = normalizedNodes.map((node) => {
    const id = node.id;
    const shouldStrip =
      id === undefined ||
      id === null ||
      id === '' ||
      (typeof id === 'string' && id.startsWith('node-'));
    if (shouldStrip) {
      const { id: _omit, ...rest } = node;
      return rest;
    }
    return node;
  });

  const basePayload = {
    name: route?.name ?? 'Draft route',
    nodes: payloadNodes,
    encodedPolyline: route?.encodedPolyline ?? '',
    routeOptimization: ['fastest', 'shortest', 'traffic_aware'].includes(route?.routeOptimization)
      ? route.routeOptimization
      : 'fastest',
  };

  const payload = { ...extras, ...basePayload };
  return payload;
};

/**
 * Persist a responder tour to the backend using the TravelRoute/TravelNode shape.
 * @param {TravelRoute} route
 * @returns {Promise<any>}
 */
export async function saveTourRoute(route) {
  const payload = buildRoutePayload(route);

  const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
  const useProxy = origin.includes('localhost') || origin.includes('127.0.0.1');
  const path = '/responder/tours';
  const absoluteEndpoint = `${BASE_URL}${path}`;
  const endpoint = useProxy ? path : absoluteEndpoint;

  try {
    const response = await apiClient.post(endpoint, payload);
    return response?.data;
  } catch (err) {
    const status = err?.response?.status;
    const text =
      typeof err?.response?.data === 'string'
        ? err.response.data
        : err?.response?.data?.message || err?.message || 'Unknown error';
    const msg = `Failed to save tour: ${status || ''} ${text}`.trim();
    console.error('saveTourRoute error', msg, {
      endpoint,
      payload,
      status,
      data: err?.response?.data,
    });
    throw new Error(msg);
  }
}

/**
 * Update an existing tour.
 * @param {string|number} id
 * @param {TravelRoute} route
 * @returns {Promise<any>}
 */
export async function updateTourRoute(id, route) {
  const payload = buildRoutePayload(route);

  const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
  const useProxy = origin.includes('localhost') || origin.includes('127.0.0.1');
  const path = `/responder/tours/${id}`;
  const absoluteEndpoint = `${BASE_URL}${path}`;
  const endpoint = useProxy ? path : absoluteEndpoint;

  try {
    const response = await apiClient.put(endpoint, payload);

    return response?.data;
  } catch (err) {
    const status = err?.response?.status;
    const text =
      typeof err?.response?.data === 'string'
        ? err.response.data
        : err?.response?.data?.message || err?.message || 'Unknown error';
    const msg = `Failed to update tour: ${status || ''} ${text}`.trim();
    console.error('updateTourRoute error', msg, {
      endpoint,
      payload,
      status,
      data: err?.response?.data,
    });
    throw new Error(msg);
  }
}
